import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, Printer, AlertCircle, Download } from 'lucide-react';
import { BillingInfo, PaymentRecord } from '../lib/billing-hooks';
export function InvoicePage() {
  const {
    paymentId
  } = useParams<{
    paymentId: string;
  }>();
  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [tableLabel, setTableLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!paymentId) return;
      try {
        const {
          data: pay,
          error: payErr
        } = await supabase.from('payments').select('*').eq('id', paymentId).maybeSingle();
        if (payErr) throw payErr;
        if (!pay) throw new Error('Factura no encontrada');
        const {
          data: billing,
          error: bErr
        } = await supabase.from('restaurant_billing_info').select('*').eq('restaurant_id', pay.restaurant_id).maybeSingle();
        if (bErr) throw bErr;
        if (!billing) throw new Error('Datos del comercio no disponibles');
        let tLabel: string | null = null;
        if (pay.table_id) {
          const {
            data: tbl
          } = await supabase.from('tables').select('table_number, table_name').eq('id', pay.table_id).maybeSingle();
          if (tbl) tLabel = tbl.table_name || `Mesa ${tbl.table_number}`;
        }
        if (mounted) {
          setPayment(pay as PaymentRecord);
          setInfo(billing as BillingInfo);
          setTableLabel(tLabel);
        }
      } catch (err: any) {
        if (mounted) setError(err.message || 'Error al cargar la factura');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [paymentId]);
  const fmt = (n: number) => new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 0
  }).format(n);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-5649">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-id="element-5650" />
      </div>;
  }
  if (error || !payment || !info) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6" data-id="element-5651">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg" data-id="element-5652">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" data-id="element-5653" />
          <h1 className="text-xl font-black text-gray-900 mb-1" data-id="element-5654">
            Factura no disponible
          </h1>
          <p className="text-sm text-gray-500" data-id="element-5655">
            {error || 'No se pudo cargar el documento.'}
          </p>
        </div>
      </div>;
  }
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
  const consecStr = String(payment.consecutive_number).padStart(6, '0');
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  return <div className="min-h-screen bg-gray-100 py-6 px-4 print:bg-white print:p-0" data-id="element-5656">
      <style data-id="element-5657">{`
        @media print {
          @page { margin: 8mm; size: 80mm auto; }
          body { background: white !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-sm mx-auto" data-id="element-5658">
        {/* Action bar */}
        <div className="no-print flex gap-2 mb-4" data-id="element-5659">
          <button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 text-white font-bold text-sm shadow-lg hover:bg-gray-800 transition-colors" data-id="element-5660">
            <Printer className="w-4 h-4" data-id="element-5661" />
            Imprimir / Guardar PDF
          </button>
        </div>

        <p className="no-print text-xs text-gray-500 text-center mb-4" data-id="element-5662">
          💡 En el diálogo de impresión, elige{' '}
          <strong data-id="element-5663">"Guardar como PDF"</strong> para descargar la factura.
        </p>

        {/* Receipt */}
        <div className="bg-white p-5 shadow-md font-mono text-gray-900 border border-gray-200 print:border-0 print:shadow-none" data-id="element-5664">
          <h1 className="text-base font-black text-center tracking-wide mb-1" data-id="element-5665">
            {info.business_name}
          </h1>
          <div className="text-[11px] text-center leading-snug mb-3" data-id="element-5666">
            <strong className="block" data-id="element-5667">
              Cédula {info.legal_id_type === 'fisica' ? 'Física' : 'Jurídica'}:{' '}
              {info.legal_id}
            </strong>
            {info.address && <>
                {info.address}
                <br data-id="element-5668" />
              </>}
            {info.phone && <>Tel: {info.phone}</>}
            {info.email && <> · {info.email}</>}
          </div>

          <div className="text-[11px] border-y border-dashed border-gray-400 py-2 my-2 space-y-0.5" data-id="element-5669">
            <div className="flex justify-between" data-id="element-5670">
              <span data-id="element-5671">Factura N°</span>
              <strong data-id="element-5672">
                {consecStr}
                {payment.is_partial && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[9px] font-bold" data-id="element-5673">
                    PARCIAL
                  </span>}
              </strong>
            </div>
            <div className="flex justify-between" data-id="element-5674">
              <span data-id="element-5675">Fecha</span>
              <span data-id="element-5676">{date}</span>
            </div>
            {tableLabel && <div className="flex justify-between" data-id="element-5677">
                <span data-id="element-5678">Mesa</span>
                <span data-id="element-5679">{tableLabel}</span>
              </div>}
          </div>

          <table className="w-full text-[11px] my-2" data-id="element-5680">
            <thead data-id="element-5681">
              <tr className="border-b border-gray-900 uppercase text-[10px]" data-id="element-5682">
                <td data-id="element-5683">Cant</td>
                <td data-id="element-5684">Descripción</td>
                <td className="text-right" data-id="element-5685">Total</td>
              </tr>
            </thead>
            <tbody data-id="element-5686">
              {(payment.items_json || []).map((it, idx) => <tr key={idx} data-id="element-5687">
                  <td className="font-bold w-8" data-id="element-5688">{it.quantity}×</td>
                  <td className="pr-2" data-id="element-5689">
                    {it.item_name}
                    {it.notes && <div className="text-[10px] text-gray-500 italic" data-id="element-5690">
                        {it.notes}
                      </div>}
                  </td>
                  <td className="text-right tabular-nums whitespace-nowrap" data-id="element-5691">
                    {fmt(it.line_total)}
                  </td>
                </tr>)}
            </tbody>
          </table>

          <div className="border-t border-dashed border-gray-400 pt-2 mt-2" data-id="element-5692">
            <div className="flex justify-between text-sm font-black border-t-2 border-gray-900 pt-2 mt-1" data-id="element-5693">
              <span data-id="element-5694">TOTAL</span>
              <span data-id="element-5695">{fmt(payment.amount)}</span>
            </div>
          </div>

          <div className="flex justify-between text-[11px] border-t border-dashed border-gray-400 pt-2 mt-2" data-id="element-5696">
            <span data-id="element-5697">Forma de pago</span>
            <strong data-id="element-5698">{methodLabel}</strong>
          </div>

          {info.footer_note && <p className="text-center text-[11px] mt-3 text-gray-700" data-id="element-5699">
              {info.footer_note}
            </p>}
          <p className="text-center text-[9px] mt-2 text-gray-500 leading-tight" data-id="element-5700">
            Régimen Tributario Simplificado
            <br data-id="element-5701" />
            Documento equivalente — no constituye factura electrónica
          </p>
        </div>
      </div>
    </div>;
}